import { MenuComprador } from '../interfaces/menu-comprador.interface';

/** Nodo mínimo para comparación semántica estable (orden de menú preservado). */
export interface CanonicalMenuNode {
  label: string;
  redirect?: string;
  children?: CanonicalMenuNode[];
}

function readMenuRecord(item: MenuComprador): Record<string, unknown> {
  return item as Record<string, unknown>;
}

function readStringField(
  record: Record<string, unknown>,
  keys: readonly string[],
): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return '';
}

function readChildren(record: Record<string, unknown>): MenuComprador[] {
  for (const key of ['subMenu', 'hijos', 'children'] as const) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value as MenuComprador[];
    }
  }
  return [];
}

/**
 * Devuelve los hijos de un nodo de menú en cualquier variante (subMenu, hijos, children).
 * Tras `normalizeMenuCompradorArray`, los anidados quedan en `subMenu`.
 */
export function readMenuCompradorChildren(
  item: MenuComprador,
): MenuComprador[] | undefined {
  const children = readChildren(readMenuRecord(item));
  return children.length > 0 ? children : undefined;
}

function isMenuCompradorLike(value: unknown): value is MenuComprador {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Normaliza la respuesta del API/caché a un array seguro de `MenuComprador`.
 * Descarta entradas no objeto; unifica hijos en `subMenu` para persistencia consistente.
 */
export function normalizeMenuCompradorArray(input: unknown): MenuComprador[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const result: MenuComprador[] = [];
  for (const entry of input) {
    if (!isMenuCompradorLike(entry)) {
      continue;
    }
    const normalized = normalizeMenuCompradorItem(entry);
    if (normalized) {
      result.push(normalized);
    }
  }
  return result;
}

function normalizeMenuCompradorItem(item: MenuComprador): MenuComprador | null {
  const record = readMenuRecord(item);
  const label = readStringField(record, ['label', 'nombre', 'Label', 'Nombre']);
  const redirect = readStringField(record, [
    'redirect',
    'url',
    'Redirect',
    'Url',
  ]);
  const childSource = readChildren(record);
  const subMenu =
    childSource.length > 0
      ? normalizeMenuCompradorArray(childSource)
      : undefined;

  if (!label && !redirect && (!subMenu || subMenu.length === 0)) {
    return null;
  }

  const normalized: MenuComprador = {
    label: label || undefined,
    nombre: label || undefined,
    redirect: redirect || undefined,
    url: redirect || undefined,
  };
  if (subMenu && subMenu.length > 0) {
    normalized.subMenu = subMenu;
  }
  return normalized;
}

/** Convierte el menú a nodos canónicos para comparación SWR. */
export function toCanonicalMenuNodes(items: MenuComprador[]): CanonicalMenuNode[] {
  return items.map((item) => {
    const record = readMenuRecord(item);
    const label = readStringField(record, ['label', 'nombre', 'Label', 'Nombre']);
    const redirect = readStringField(record, [
      'redirect',
      'url',
      'Redirect',
      'Url',
    ]);
    const childrenSource = readChildren(record);
    const children =
      childrenSource.length > 0
        ? toCanonicalMenuNodes(normalizeMenuCompradorArray(childrenSource))
        : undefined;

    const node: CanonicalMenuNode = { label };
    if (redirect) {
      node.redirect = redirect;
    }
    if (children && children.length > 0) {
      node.children = children;
    }
    return node;
  });
}

/** Representación JSON estable del menú para comparar caché vs API. */
export function canonicalMenuRaw(items: MenuComprador[]): string {
  return JSON.stringify(toCanonicalMenuNodes(normalizeMenuCompradorArray(items)));
}

/** Indica si dos payloads de menú son equivalentes para el usuario (label, redirect, árbol). */
export function menusAreSemanticallyEqual(
  left: MenuComprador[],
  right: MenuComprador[],
): boolean {
  return canonicalMenuRaw(left) === canonicalMenuRaw(right);
}
